import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Building,
  AlertCircle,
  Plus,
  Activity,
  Settings,
  Bot,
  Bell,
  Landmark,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { AdminNav } from '@/components/admin/AdminNav';
import { Json } from '@/integrations/supabase/types';
import MultiStepOrganisationModal from '@/components/organisation/MultiStepOrganisationModal';
import UserActivityLogs from '@/components/admin/UserActivityLogs';
import SystemMessageEditor from '@/components/admin/SystemMessageEditor';
import SchemaManagementEditor from '@/components/admin/SchemaManagementEditor';
import AlertsManagement from '@/components/admin/AlertsManagement';
import { LenderManagement } from '@/components/admin/LenderManagement';
import { EntityManagement } from '@/components/admin/entities/EntityManagement';
import ConnectionSettings from '@/components/admin/ConnectionSettings';
import { ExportNotificationsProvider } from '@/components/data-table/ExportNotificationsProvider';
import { exportMonitoringService } from "@/utils/organization/exportMonitoringService";
import { initializeExportCleanup } from "@/utils/organization/exportCleanup";
import { monitorExportHealth } from '@/utils/organization/exportCleanupService';
import { featureFlags } from '@/utils/featureFlags';
import { MerchantsList } from '@/components/admin/merchants/MerchantsList';
import { useMerchantGmv } from '@/hooks/useMerchantGmv';

interface Organisation {
  address: string;
  connections: Json;
  created_at: string;
  ein: string;
  id: string;
  name: string;
  name_dbas: string;
  poc_email: string;
  poc_name: string;
  poc_phone?: string;
  products: Json;
  state_of_incorporation: string;
  updated_at?: string;
  status?: string;
  isDraft?: boolean;
  draftId?: string;
  merchant_type: string;
  is_test_clone?: boolean;
  source_organisation_id?: string;
  data_cutoff_date?: string;
}

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  // ProtectedRoute requireAdmin already gates this page; reuse its result.
  const { isAdmin } = useAuth();
  // FIG-345 · UC-230: nightly GMV for the Merchants list (admin-only read).
  const merchantGmv = useMerchantGmv(isAdmin);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [activeTab, setActiveTab] = useState('organisations');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);


  const fetchOrganisationsAndDrafts = async (): Promise<boolean> => {
    if (!isAdmin) return false;

    // E2E tests fetch real organizations - global setup creates test data
    try {
      setLoading(true);

      // Fetch submitted organisations
      const { data: firstPage, error: orgsError, count: orgsCount } = await supabase
        .from('organisations')
        .select('*', { count: 'exact' });

      if (orgsError || !firstPage) throw (orgsError || new Error('Failed to load organisations and drafts'));

      // PostgREST caps a response at max_rows (1,000). The list's filter,
      // counts and totals need every merchant, so page through the rest.
      let submittedOrgs = firstPage;
      if (orgsCount != null && orgsCount > firstPage.length) {
        const pageSize = firstPage.length || 1000;
        const allOrgs: typeof firstPage = [];
        for (let from = 0; from < orgsCount; from += pageSize) {
          const { data: page, error: pageError } = await supabase
            .from('organisations')
            .select('*')
            .order('id')
            .range(from, from + pageSize - 1);
          if (pageError || !page) throw (pageError || new Error('Failed to load organisations and drafts'));
          allOrgs.push(...page);
          if (page.length < pageSize) break;
        }
        submittedOrgs = allOrgs;
      }

      // Process submitted organisations
      const processedSubmittedOrgs: Organisation[] = (submittedOrgs || []).map((org: any) => ({
        address: org.address,
        connections: org.connections,
        created_at: org.created_at,
        ein: org.ein,
        id: org.id,
        name: org.name,
        name_dbas: org.name_dbas || '',
        poc_email: org.poc_email,
        poc_name: org.poc_name,
        poc_phone: org.poc_phone || undefined,
        products: org.products,
        state_of_incorporation: org.state_of_incorporation,
        status: org.status || 'submitted',
        isDraft: false,
        merchant_type: org.merchant_type || 'direct',
        is_test_clone: org.is_test_clone || false,
        source_organisation_id: org.source_organisation_id || undefined
      }));

      // Sort by most recent update
      processedSubmittedOrgs.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB.getTime() - dateA.getTime();
      });

      setOrganisations(processedSubmittedOrgs);
      return true;
    } catch (error) {
      setError('Failed to load organisations and drafts');
      toast({
        title: 'Error',
        description: 'Failed to load organisations and drafts',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchOrganisationsAndDrafts();

      // Initialize export services for admin dashboard
      initializeExportCleanup();
      exportMonitoringService.startAutomaticCleanup();

      // Start periodic health monitoring
      const healthMonitorInterval = setInterval(monitorExportHealth, 60000);

      return () => {
        exportMonitoringService.stopAutomaticCleanup();
        if (healthMonitorInterval) {
          clearInterval(healthMonitorInterval);
        }
      };
    }
  }, [isAdmin, toast]);

  const refreshData = async (silent: boolean = false) => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      // fetchOrganisationsAndDrafts shows its own error toast.
      const refreshed = await fetchOrganisationsAndDrafts();
      if (refreshed && !silent) {
        toast({
          title: 'Success',
          description: 'Data refreshed successfully',
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOrganisationClick = (org: Organisation) => {
    if (org.isDraft) {
      navigate(`/organisation/${org.id}?status=draft`);
    } else {
      navigate(`/organisation/${org.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600">Loading admin dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-lg text-gray-600">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleSelectInput = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    input.select();
  };

  return (
    <ExportNotificationsProvider enabled={isAdmin}>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <AdminNav onRefresh={() => refreshData()} isRefreshing={isRefreshing} />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="organisations" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Merchants
              </TabsTrigger>
              {featureFlags.showAdminEntities && (
                <TabsTrigger value="entities" className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Entities
                </TabsTrigger>
              )}
              {featureFlags.showAdminLenders && (
                <TabsTrigger value="lenders" className="flex items-center gap-2">
                  <Landmark className="h-4 w-4" />
                  Lenders
                </TabsTrigger>
              )}
              <TabsTrigger value="activity-logs" data-testid="activity-logs" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                User Activity
              </TabsTrigger>
              {featureFlags.showAdminAlerts && (
                <TabsTrigger value="alerts" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Alerts
                </TabsTrigger>
              )}
              <TabsTrigger value="fig-bot-settings" data-testid="system-messages" className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Fig Bot Settings
              </TabsTrigger>
              <TabsTrigger value="connections" data-testid="connections-tab" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Connections
              </TabsTrigger>
            </TabsList>

            <TabsContent value="organisations" className="space-y-4">
              <MerchantsList
                merchants={organisations}
                onOpen={handleOrganisationClick}
                gmv={{
                  state: merchantGmv.isError ? 'error' : merchantGmv.isSuccess ? 'ready' : 'loading',
                  data: merchantGmv.data ?? null,
                }}
                headerAction={
                  <Button
                    onClick={() => setCreateModalOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Merchant
                  </Button>
                }
              />
            </TabsContent>

            {featureFlags.showAdminEntities && (
              <TabsContent value="entities" className="space-y-4">
                <EntityManagement />
              </TabsContent>
            )}

            {featureFlags.showAdminLenders && (
              <TabsContent value="lenders" className="space-y-4">
                <LenderManagement />
              </TabsContent>
            )}

            <TabsContent value="activity-logs" className="space-y-4">
              <UserActivityLogs />
            </TabsContent>

            {featureFlags.showAdminAlerts && (
              <TabsContent value="alerts" className="space-y-4">
                <AlertsManagement />
              </TabsContent>
            )}

            <TabsContent value="fig-bot-settings" className="space-y-4">
              <div className="space-y-6">
                {/* System Settings Section */}
                <div className="bg-white rounded-md shadow">
                  <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold">System Configuration</h2>
                    <p className="text-gray-600 mt-1">Manage AI assistant system messages and global settings</p>
                  </div>
                  <div className="p-6">
                    <SystemMessageEditor />
                  </div>
                </div>

                {/* Schema Management Section */}
                <div className="bg-white rounded-md shadow">
                  <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold">Schema Management</h2>
                    <p className="text-gray-600 mt-1">Manage database schema configurations and data connections</p>
                  </div>
                  <div className="p-6">
                    <SchemaManagementEditor />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connections" className="space-y-4">
              <ConnectionSettings />
            </TabsContent>

          </Tabs>
        </main>

        {/* Create Organisation Modal */}
        <MultiStepOrganisationModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onSuccess={refreshData}
        />

      </div>
    </ExportNotificationsProvider>
  );
};

export default Admin;
